import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'What do Devon\'s AMBER DfT scorecards mean for my claim?',
    answer: 'Devon County Council earns AMBER overall, on condition and on best practice — with a RED spend scorecard. Those ratings reflect network-level trends in the Government\'s 2025/26 assessment. Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on headline scorecards alone. Devon\'s own transparency report adds context: U-road red-condition share rose from 16% in 2021 to 25% in 2024, and the council openly describes "managing a decline in the more minor network."',
  },
  {
    question: 'What is Devon\'s "managed decline" approach?',
    answer: 'Devon\'s transparency report states that maintenance budgets "provide insufficient funding to meet the annual cost of deterioration" and that the council allocates appropriate funding to the strategic network "while managing a decline in the more minor network." U-roads make up 52% of the 13,000km network (6,789km). That is published council policy — not an allegation — and may be relevant where your incident was on a residential lane, village street or other unclassified road.',
  },
  {
    question: 'Why is the DfT Spend scorecard RED when Devon spends over £105m on highways?',
    answer: 'The DfT Spend scorecard is separate from Devon\'s published budget tables. In 2024/25 the council recorded £74.523m capital spend and £31.386m revenue spend — £105.909m total — against a DfT capital allocation of £60.876m. For 2025/26, £22.523m of the £83.64m total DfT allocation is "subject to incentive criteria." Aggregate spend does not prove the individual pothole was known and repaired within inspection intervals; your road class, prior reports and photos matter more.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified (U) roads account for 6,789km — 52% of Devon\'s network. SCANNER surveys show the share of U-roads in the red category rose from 16% in 2021 to 25% in 2024. U-road condition data is collected over a four-year cycle (50% one direction per year). If your incident was on a U-road, the council\'s published managed-decline language and deteriorating U-road trend may support a claim — but you still need evidence of the specific defect, including size, location and when it formed.',
  },
  {
    question: 'Devon filled 56,328 potholes in 2024/25 — does that mean my road was maintained?',
    answer: 'No. Those figures include safety and non-safety defects passed to Devon\'s term contractor, ranging from regular potholes (up to 1sqm) to larger patches. The council projects 58,824 repairs in 2025/26. High reactive volume on England\'s largest road network is evidence of ongoing defect formation — not proof that your specific pothole was unavoidable or that the council had no duty to act on it sooner.',
  },
  {
    question: 'Does Devon\'s SCANNER data caveat affect how I should read condition statistics?',
    answer: 'Yes. Devon uses SCANNER laser surveys across all road classes — unusually comprehensive for a county where 52% of roads are unclassified. The council warns that SCANNER measures "may not fully align with the user perception of road condition" and that maintenance is "not solely determined by the result of these surveys." Other factors, including local priorities and risk-based asset management, determine where work is carried out. Survey snapshots may not reflect the condition of your road on the day of your incident.',
  },
  {
    question: 'How do I report a pothole to Devon County Council?',
    answer: 'Report potholes via the council\'s online form at devon.gov.uk/roads-and-transport/report-a-problem/report-a-pothole/. Search the map first — if the defect is already logged, select "Add me to this pothole" rather than creating a duplicate. For emergencies presenting an imminent threat to life or serious injury, call 0345 155 1004. Prior reports strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function DevonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Devon Pothole Claims | 56,328 Repairs on England's Largest Network | Fixtyer"
        description="Devon County Council maintains 13,000km of roads — 52% unclassified — with AMBER DfT scorecards, RED spend, and a published 'managed decline' strategy on minor roads. U-road red condition rose from 16% to 25% since 2021. What that means for your Section 58 claim."
        canonical="/councils/devon"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Devon', url: '/councils/devon' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Devon County Council"
        councilSlug="devon"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/devon"
        headline="Devon Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Devon</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            56,328 Pothole Repairs in 2024/25 on England&apos;s Largest Road Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Devon County Council maintains <strong className="text-white">13,000km</strong> of roads — the largest
            network of any English highway authority — with <strong className="text-amber-400">AMBER</strong> DfT
            scorecards on overall performance, condition and best practice, and a{' '}
            <strong className="text-rose-400">RED</strong> spend scorecard. Its own report records{' '}
            <strong className="text-rose-400">56,328 pothole repairs in 2024/25</strong>, admits{' '}
            <strong className="text-white">"managing a decline in the more minor network,"</strong> and shows
            U-road red condition rising from <strong className="text-amber-400">16% in 2021 to 25% in 2024</strong>.
            Section 58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">52%</div>
            <div className="text-white font-medium mt-2">Of the network is unclassified U-roads</div>
            <div className="text-brand-300 text-sm mt-1">
              6,789km of residential lanes, village streets and rural access roads — where the council&apos;s
              transparency report describes managed decline, while planning just 14km of B, C and U class
              resurfacing in 2025/26.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Department for Transport Scorecards 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            Official ratings from the Government&apos;s local road maintenance assessment
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full">
              <caption className="sr-only">Devon County Council DfT road maintenance scorecards 2025 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="red" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-4">
            <p className="text-amber-800 text-sm">
              <strong>How to read this:</strong> DfT scorecards measure network-level condition, spending patterns
              and maintenance practice — not whether a specific pothole on your road was reasonably inspected.
              Devon&apos;s RED spend scorecard sits alongside three AMBER ratings. For a claim, combine these
              official assessments with evidence of your defect: photos, repair invoices, road class, and any
              prior reports you or others submitted.
            </p>
          </div>

          <p className="text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            13,000km — England&apos;s Largest Highway Network
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Devon&apos;s local highways maintenance transparency report — where pothole claims happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">13,000km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total roads</div>
              <div className="text-xs text-surface-500 mt-1">Largest in England</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">6,789km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">52% of the network</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">5,170km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
              <div className="text-xs text-surface-500 mt-1">40% of carriageway</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,041km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">8% of carriageway</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Devon highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">2,806km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges</td>
                  <td className="px-6 py-4 text-center font-medium">3,079</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">212,742</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Urban vs rural carriageway</td>
                  <td className="px-6 py-4 text-center font-medium">2,916km urban; 77% rural</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Roads of all types in Devon are heavily affected by seasonal variations in traffic volumes with flows increasing significantly during the summer months. This contributes to congestion, particularly on our principal routes, and increases carriageway deterioration on our minor roads."
            source="Devon County Council Local Highways Maintenance Transparency Report"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the Published Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data — Devon reports condition on all road classes, not just A, B and C
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> Devon uses SCANNER laser surveys across A, B, C and U roads.
              The council states SCANNER data &quot;may not fully align with the user perception of road condition&quot;
              and that maintenance is &quot;not solely determined by the result of these surveys.&quot; U-road condition
              is collected over a four-year cycle (50% one direction per year). From 2026/27, PAS 2161 will replace
              the current three-category red/amber/green system.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (1,041km) — surveyed over two years</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">30%</td>
                    <td className="px-4 py-2 text-center">67%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">3%</td>
                    <td className="px-4 py-2 text-center font-bold">27%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">70%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road condition has stayed broadly stable — 70% green in 2024. Principal routes are a small share
              of the network at 8%.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (5,170km) — 40% of the network</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">14%</td>
                    <td className="px-4 py-2 text-center">36%</td>
                    <td className="px-4 py-2 text-center">50%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">10%</td>
                    <td className="px-4 py-2 text-center font-bold">36%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">54%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C red condition fell from 14% to 10% between 2020 and 2024 — modest improvement on the strategic
              network the council prioritises in its hierarchy.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">Unclassified roads (6,789km) — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red (% requiring maintenance)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">18%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">16%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">20%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">23%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">25%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              U-road red condition rose from 16% in 2021 to 25% in 2024 — roughly 1,697km of residential and rural
              network in the category the council defines as &quot;should be considered for maintenance.&quot; The
              council links this trend to its managed-decline strategy on the minor network.
            </p>
          </div>

          <QuoteCard
            quote="Maintenance budgets provide insufficient funding to meet the annual cost of deterioration and consequently the condition of certain elements of the highway asset will get worse. Appropriate funding levels are allocated to the more strategic network to try to maintain their current conditions levels, while managing a decline in the more minor network."
            source="Devon County Council Local Highways Maintenance Transparency Report"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Rising Pothole Repair Volumes
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Estimated potholes filled — defects passed to Devon&apos;s term maintenance contractor, including safety
            and non-safety repairs
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Devon County Council estimated potholes filled 2021 to 2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Potholes filled</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Change vs 2023/24 trough</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">55,591</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">47,324</td>
                  <td className="px-4 py-3 text-center text-emerald-400">-11.8%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">42,329</td>
                  <td className="px-4 py-3 text-center text-surface-400">Baseline trough</td>
                </tr>
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-mono font-bold">2024/25</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-400">56,328</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-400">+33.1%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">58,824</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-400">+39.0%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="Despite the Council's best efforts to invest in a proactive preventative approach, funding levels and impacts such as inflation and climate change has over time meant that the need for reactive maintenance is becoming more prevalent, leading to a rise in unplanned and disruptive maintenance."
              source="Devon County Council Local Highways Maintenance Transparency Report"
            />
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">More than 150 repairs every day</h3>
              <p className="text-brand-200 text-sm">
                56,328 pothole repairs in 2024/25 works out to roughly 154 patches per day across 13,000km.
                Repairs fell to 42,329 in 2023/24 before surging 33% the following year — a pattern the council
                links to rising reactive demand, not improved road condition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Following the Money
          </h2>
          <p className="text-surface-600 mb-8">
            Highway maintenance spending from Devon&apos;s transparency report — with £22.523m of 2025/26 funding
            subject to incentive criteria
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Devon highway maintenance spending 2020 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                <tr className="bg-amber-50">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">83,640*</td>
                  <td className="px-4 py-3 text-center font-bold">83,166</td>
                  <td className="px-4 py-3 text-center">31,183</td>
                  <td className="px-4 py-3 text-center text-emerald-600">90%</td>
                  <td className="px-4 py-3 text-center">10%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">60,876</td>
                  <td className="px-4 py-3 text-center">74,523</td>
                  <td className="px-4 py-3 text-center">31,386</td>
                  <td className="px-4 py-3 text-center">89%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">68,877</td>
                  <td className="px-4 py-3 text-center">70,658</td>
                  <td className="px-4 py-3 text-center">29,179</td>
                  <td className="px-4 py-3 text-center">89%</td>
                  <td className="px-4 py-3 text-center">11%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">75,001</td>
                  <td className="px-4 py-3 text-center">69,247</td>
                  <td className="px-4 py-3 text-center">25,935</td>
                  <td className="px-4 py-3 text-center text-emerald-600">95%</td>
                  <td className="px-4 py-3 text-center">5%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-xs text-surface-500 mb-6">
            * 2025/26 total DfT allocation of £83.64m includes £22.523m &quot;subject to incentive criteria&quot;;
            baseline allocation is £61.117m. Reactive maintenance includes safety defects, incident reaction and emergencies.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-rose-50 rounded-xl p-6 border border-rose-200">
              <h3 className="font-semibold text-rose-900 mb-3">RED spend scorecard — and Devon&apos;s own funding gap</h3>
              <p className="text-rose-800 text-sm">
                The DfT Spend scorecard for Devon is RED for 2025/26 — alongside AMBER on overall, condition and
                best practice. Devon&apos;s transparency report separately states maintenance budgets &quot;do not
                provide sufficient funding to meet the annual cost of deterioration,&quot; and £22.523m of the
                2025/26 DfT allocation is subject to incentive criteria.
              </p>
            </div>
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h3 className="font-semibold text-amber-900 mb-3">Preventative share vs pothole reality</h3>
              <p className="text-amber-800 text-sm">
                Estimated preventative maintenance rose from 95% in 2020/21 to a projected 90% in 2025/26 — yet
                pothole fills surged from a 2023/24 trough of 42,329 to a projected 58,824. Higher preventative
                percentages alongside rising reactive repairs suggest defects are still forming faster than the
                minor network is stabilised.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What Devon says it will deliver this financial year — programmes subject to change
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">325km</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Surface dressing</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">23km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A class resurfacing</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">14km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">B, C and U resurfacing</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">~59,000</div>
              <div className="text-sm text-rose-800 font-medium mt-1">Potholes (estimated)</div>
            </div>
          </div>

          <QuoteCard
            quote="The Council will continue to prioritise available funding on the more strategic parts of the network, such as A and B carriageway classes, with the remaining budget spread across Devon's lengthy road network of 'C' and unclassified roads."
            source="Devon County Council Local Highways Maintenance Transparency Report"
          />

          <div className="mt-6 bg-white rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths on U-roads</h3>
            <p className="text-surface-600 text-sm">
              At 25% red condition, roughly 1,697km of U-roads should be considered for maintenance. The council
              plans 14km of B, C and U class resurfacing in 2025/26 — structural treatment for a tiny fraction of
              the minor network. Programmes are subject to change due to financial pressures and climate change,
              per the council&apos;s own caveat.
            </p>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Devon says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey and inspection approach</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>All road classes:</strong> SCANNER laser surveys (A over two years; B over two years; C over four years; U over four years)</li>
                <li>• <strong>Public reports:</strong> pothole reports assessed on-site by trained highway safety inspectors</li>
                <li>• <strong>Priority potholes:</strong> 40mm vertical edge and 300mm width on roads — assessed within seven calendar days</li>
                <li>• <strong>Hierarchy:</strong> strategic A and B roads prioritised; C and U roads share remaining budget</li>
              </ul>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Pothole definition</h3>
              <p className="text-surface-600 text-sm mb-3">
                Devon categorises repairs by size: regular potholes (up to 1sqm), pothole patches (1–4sqm) and
                large pothole patches (4sqm+). Reported figures include both safety and serviceability defects.
              </p>
              <p className="text-surface-600 text-sm">
                The council states it prioritises funding and cannot afford to fix every reported problem — a
                relevant context if your defect was reported but not repaired before your incident.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Devon must
              maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              network-level SCANNER data or DfT scorecards.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the four-year U-road survey cycle — and had it been surveyed recently?</li>
              <li>• Did the defect meet investigatory criteria during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, council portal) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER-Rated County Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Devon invests heavily on strategic routes — but its own data shows minor-road decline
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition stable at 70% green (2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C red condition improved from 14% to 10% (2020–2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Comprehensive SCANNER surveys on all road classes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £105.9m total highways spend in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management and innovation programme
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a structured Section 58 defence on A-roads and recently resurfaced strategic routes.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT AMBER overall, condition and best practice; RED spend
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Published &quot;managed decline&quot; on the minor network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road red condition up from 16% (2021) to 25% (2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 56,328 pothole fills in 2024/25 — up 33% from 2023/24 trough
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Just 14km B/C/U resurfacing planned against 6,789km of U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits insufficient funding to prevent deterioration
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a county with comprehensive SCANNER data and £100m+ budgets, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road class</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, managed-decline policy and rising red condition are structural arguments</li>
              <li>• Whether the council can produce inspection records for your street, not just network-wide averages</li>
            </ul>
            <p className="text-white font-medium">
              Fixtyer builds exactly this case: prior-report search, photo assessment, and citations from Devon&apos;s
              own transparency data where it helps you — without overstating what DfT scorecards alone prove.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Devon County Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Search the map first — if the pothole is
            already logged, select &quot;Add me to this pothole&quot; rather than creating a duplicate. Tell us
            when you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.devon.gov.uk/roads-and-transport/report-a-problem/report-a-pothole/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — devon.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-xs mt-4">
            Emergencies presenting an imminent threat to life or serious injury: call{' '}
            <strong className="text-surface-700">0345 155 1004</strong>
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Devon?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            England&apos;s largest network demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No managed-decline citation</li>
                <li>• No prior-report search</li>
                <li>• No U-road deterioration data</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ Managed-decline admission cited</li>
                <li>✅ U-road red condition trend documented</li>
                <li>✅ 56,328 pothole repairs in 2024/25 referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Devon</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=devon"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Devon Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.devon.gov.uk/roads-and-transport/maintaining-roads/local-highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Devon County Council Local Highways Maintenance Transparency Report
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
